import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { apiFetch, registerTokenLoader, setAccessToken } from './api';

const TOKEN_KEY = 'lpr.parent.accessToken';

export interface MeResponse {
  userId: string;
  userType: string;
  displayName: string;
  parentGuardianId: string | null;
  schoolId: string | null;
}

export interface PermissionsResponse {
  permissions: string[];
  scope: {
    type: string;
    parentGuardianId?: string;
  };
}

interface AuthContextValue {
  token: string | null;
  me: MeResponse | null;
  permissions: PermissionsResponse | null;
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
  can: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadSession(token: string): Promise<{
  me: MeResponse;
  permissions: PermissionsResponse;
}> {
  setAccessToken(token);
  const [me, permissions] = await Promise.all([
    apiFetch<MeResponse>('/me'),
    apiFetch<PermissionsResponse>('/me/permissions'),
  ]);
  return { me, permissions };
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState<MeResponse | null>(null);
  const [permissions, setPermissions] = useState<PermissionsResponse | null>(null);
  const [loading, setLoading] = useState(Boolean(token));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMe(null);
    setPermissions(null);
    setAccessToken(null);
  }, []);

  const login = useCallback(async (nextToken: string) => {
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
    const session = await loadSession(nextToken);
    setMe(session.me);
    setPermissions(session.permissions);
  }, []);

  const refreshMe = useCallback(async () => {
    if (!token) return;
    const session = await loadSession(token);
    setMe(session.me);
    setPermissions(session.permissions);
  }, [token]);

  useEffect(() => {
    registerTokenLoader(() => localStorage.getItem(TOKEN_KEY));
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    loadSession(token)
      .then((session) => {
        if (cancelled) return;
        setMe(session.me);
        setPermissions(session.permissions);
      })
      .catch(() => {
        if (cancelled) return;
        logout();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  const value = useMemo<AuthContextValue>(() => ({
    token,
    me,
    permissions,
    loading,
    login,
    logout,
    refreshMe,
    can: (code) => permissions?.permissions.includes(code) ?? false,
  }), [token, me, permissions, loading, login, logout, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function usePermission(code: string): boolean {
  return useAuth().can(code);
}
